import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FCX, SelectComponent } from '@datassential/platform-ui-lib';
import cn from 'classnames';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import CustomScrollbarsVirtualList from '@components/CustomScrollbarsVirtualList/CustomScrollbarsVirtualList';
import { hasMatches, highlightMatches, prepareForMatching } from '@helpers';
import Button from '@components/new/Button/Button';
import { components } from '@apiSchema';
import {
    CHAIN_MENU_GAP_TITLE,
    CUSTOM_MENU_GAP_TITLE,
    CUSTOM_MENU_GAP_DESC,
} from './HomeSalesFocusChains.constants';
import GapAnalysisModal from '@pages/_shared/GapAnalysisModal/GapAnalysisModal';

type ChainOption = components['schemas']['ChainOption'];

interface ChainMenuGapProps {
    chains: ChainOption[];
}

interface ChainMenuGapOption {
    id: number;
    name: string;
}

const ITEM_HEIGHT = 40;
const MAX_LIST_HEIGHT = 320;

interface ChainRowData {
    options: ChainMenuGapOption[];
    selectedChainId: number | null;
    searchQuery: string;
    onSelect: (chain: ChainMenuGapOption) => void;
}

const ChainRow = ({ index, style, data }: ListChildComponentProps<ChainRowData>) => {
    const chain = data.options[index];
    return (
        <div
            style={style}
            className={cn(
                'ChainMenuGap__dropdown-item',
                data.selectedChainId === chain.id && 'is-selected',
            )}
            onClick={() => data.onSelect(chain)}
            data-testid={`buttonChainMenuGapChain_${chain.id}`}
        >
            <span
                dangerouslySetInnerHTML={{
                    __html: highlightMatches(
                        chain.name,
                        data.searchQuery,
                        (m) => `<span class="ChainMenuGap__dropdown-highlight">${m}</span>`,
                    ),
                }}
            />
        </div>
    );
};

const ChainMenuGap: FCX<ChainMenuGapProps> = ({ chains }) => {
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedChain, setSelectedChain] = useState<ChainMenuGapOption | null>(null);
    const [isGapModalOpen, setIsGapModalOpen] = useState(false);

    useEffect(() => {
        if (isDropdownOpen && searchInputRef.current) {
            requestAnimationFrame(() => searchInputRef.current!.focus());
        }
    }, [isDropdownOpen]);

    const chainOptions = useMemo<ChainMenuGapOption[]>(
        () => chains
            .filter((c) => c.chainId != null)
            .map((c) => ({ id: Number(c.chainId), name: c.chainName ?? '' })),
        [chains],
    );

    const filteredOptions = useMemo(() => {
        const trimmed = prepareForMatching(searchQuery);
        const matched = trimmed === ''
            ? chainOptions
            : chainOptions.filter((c) => hasMatches(c.name, searchQuery));

        if (trimmed !== '') {
            matched.sort((a, b) => {
                const aStarts = prepareForMatching(a.name).indexOf(trimmed) === 0;
                const bStarts = prepareForMatching(b.name).indexOf(trimmed) === 0;
                if (aStarts !== bStarts) return aStarts ? -1 : 1;
                return a.name.localeCompare(b.name);
            });
        }

        return matched;
    }, [chainOptions, searchQuery]);

    const handleDropdownToggle = useCallback((open: boolean) => {
        setIsDropdownOpen(open);
        if (!open) setSearchQuery('');
    }, []);

    const handleSelect = useCallback((chain: ChainMenuGapOption) => {
        setSelectedChain(chain);
        setSearchQuery('');
        setIsDropdownOpen(false);
    }, []);

    const hasSelection = selectedChain !== null;

    const thumbnailText = selectedChain
        ? selectedChain.name
        : <span style={{ opacity: 0.6 }}>Select Chain</span>;

    return (
        <div className="ChainMenuGap">
            <h3 className="HomeSalesFocusChains__menu-gap-title">{CHAIN_MENU_GAP_TITLE}</h3>
            <div className="HomeSalesFocusChains__menu-gap-select-wrapper">
                <SelectComponent
                    isOpened={isDropdownOpen}
                    setIsOpened={handleDropdownToggle}
                    thumbnailText={thumbnailText}
                    isFluidWidth
                    fieldProps={{ modifiers: [] }}
                    dropdownProps={{
                        isOpened: isDropdownOpen,
                        place: 'bottom-start',
                        style: { display: 'block', padding: 6, minWidth: 220 },
                    }}
                >
                    <div className="ChainMenuGap__dropdown-content">
                        <input
                            ref={searchInputRef}
                            type="text"
                            className="ChainMenuGap__dropdown-search"
                            placeholder="Search for a chain..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            data-testid="inputChainMenuGapSearch"
                        />
                        <div className="ChainMenuGap__dropdown-options">
                            {filteredOptions.length === 0 && (
                                <div className="ChainMenuGap__dropdown-empty">No chains found</div>
                            )}
                            {filteredOptions.length > 0 && (
                                <div style={{ height: Math.min(filteredOptions.length * ITEM_HEIGHT, MAX_LIST_HEIGHT) }}>
                                    <AutoSizer>
                                        {({ width, height }) => (
                                            <FixedSizeList
                                                outerElementType={CustomScrollbarsVirtualList}
                                                height={height}
                                                width={width}
                                                itemCount={filteredOptions.length}
                                                itemSize={ITEM_HEIGHT}
                                                itemData={{
                                                    options: filteredOptions,
                                                    selectedChainId: selectedChain?.id ?? null,
                                                    searchQuery,
                                                    onSelect: handleSelect,
                                                }}
                                            >
                                                {ChainRow}
                                            </FixedSizeList>
                                        )}
                                    </AutoSizer>
                                </div>
                            )}
                        </div>
                    </div>
                </SelectComponent>
            </div>
            <div className="HomeSalesFocusChains__menu-gap-actions">
                {/* Category Menu Gap hidden until the underlying logic is improved
                <div className="HomeSalesFocusChains__menu-gap-block">
                    <span className="HomeSalesFocusChains__menu-gap-label">
                        {CATEGORY_MENU_GAP_TITLE}
                    </span>
                    <div className="HomeSalesFocusChains__menu-gap-desc-row">
                        <span className="HomeSalesFocusChains__menu-gap-desc">
                            {CATEGORY_MENU_GAP_DESC}
                        </span>
                        <Button
                            modifiers={['accent']}
                            isDisabled={!hasSelection}
                            isLoading={isDownloading}
                            onClick={handleDownload}
                        >
                            Download
                        </Button>
                    </div>
                </div>
                */}
                <div className="HomeSalesFocusChains__menu-gap-block">
                    <span className="HomeSalesFocusChains__menu-gap-label">
                        {CUSTOM_MENU_GAP_TITLE}
                    </span>
                    <div className="HomeSalesFocusChains__menu-gap-desc-row">
                        <span className="HomeSalesFocusChains__menu-gap-desc">
                            {CUSTOM_MENU_GAP_DESC}
                        </span>
                        <Button
                            modifiers={['accent']}
                            isDisabled={!hasSelection}
                            onClick={() => setIsGapModalOpen(true)}
                        >
                            Create
                        </Button>
                    </div>
                </div>
            </div>
            {isGapModalOpen && selectedChain && (
                <GapAnalysisModal
                    close={() => setIsGapModalOpen(false)}
                    chainId={selectedChain.id}
                    chainName={selectedChain.name}
                />
            )}
        </div>
    );
};

export default ChainMenuGap;
